import { settlementWaterFoundationManifest } from './asset-manifest';
import {
  applyIslandHomography,
  islandHomography,
  type IslandPoint,
  type IslandQuad,
} from './settlement-island-registration';

export type SettlementGroundQuad = IslandQuad;
type Triangle = readonly [IslandPoint, IslandPoint, IslandPoint];
type CanvasTransform = readonly [number, number, number, number, number, number];

export interface SettlementGroundTriangle {
  source: Triangle;
  target: Triangle;
  transform: CanvasTransform;
}

/** Caller owns the canvas/texture lifecycle; this utility creates neither DOM nor Phaser objects. */
export type SettlementGroundCanvas = Pick<
  CanvasRenderingContext2D,
  | 'save'
  | 'restore'
  | 'beginPath'
  | 'moveTo'
  | 'lineTo'
  | 'closePath'
  | 'clip'
  | 'transform'
  | 'drawImage'
>;

const unitQuad: IslandQuad = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

function triangleArea([a, b, c]: Triangle): number {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

/** Canvas affine transform exactly matches all three triangle vertices. */
function triangleTransform(source: Triangle, target: Triangle): CanvasTransform | undefined {
  const area = triangleArea(source);
  if (Math.abs(area) < 1e-8 || Math.abs(triangleArea(target)) < 1e-8) return undefined;
  const [s0, s1, s2] = source;
  const [t0, t1, t2] = target;
  const sx1 = s1.x - s0.x;
  const sy1 = s1.y - s0.y;
  const sx2 = s2.x - s0.x;
  const sy2 = s2.y - s0.y;
  const tx1 = t1.x - t0.x;
  const ty1 = t1.y - t0.y;
  const tx2 = t2.x - t0.x;
  const ty2 = t2.y - t0.y;
  const a = (tx1 * sy2 - tx2 * sy1) / area;
  const b = (ty1 * sy2 - ty2 * sy1) / area;
  const c = (tx2 * sx1 - tx1 * sx2) / area;
  const d = (ty2 * sx1 - ty1 * sx2) / area;
  const transform: CanvasTransform = [
    a,
    b,
    c,
    d,
    t0.x - a * s0.x - c * s0.y,
    t0.y - b * s0.x - d * s0.y,
  ];
  return transform.every(Number.isFinite) ? transform : undefined;
}

/**
 * Sample the same regular UV mesh through source and target homographies.
 * Quads are cyclic back/right/front/left corners, not axis-aligned bounding boxes.
 * Invalid geometry fails before any canvas mutation; work is bounded to 512 triangles.
 */
export function createSettlementGroundMesh(
  sourceQuad: SettlementGroundQuad,
  targetQuad: SettlementGroundQuad,
  subdivisions = 8,
): readonly SettlementGroundTriangle[] | undefined {
  if (!Number.isInteger(subdivisions) || subdivisions < 1 || subdivisions > 16) return undefined;
  const sourceMap = islandHomography(unitQuad, sourceQuad);
  const targetMap = islandHomography(unitQuad, targetQuad);
  if (!sourceMap || !targetMap) return undefined;
  const sourcePoints: IslandPoint[] = [];
  const targetPoints: IslandPoint[] = [];
  for (let row = 0; row <= subdivisions; row++) {
    for (let column = 0; column <= subdivisions; column++) {
      const uv = { x: column / subdivisions, y: row / subdivisions };
      const source = applyIslandHomography(sourceMap, uv);
      const target = applyIslandHomography(targetMap, uv);
      if (!source || !target) return undefined;
      sourcePoints.push(source);
      targetPoints.push(target);
    }
  }
  const triangles: SettlementGroundTriangle[] = [];
  for (let row = 0; row < subdivisions; row++) {
    for (let column = 0; column < subdivisions; column++) {
      const back = row * (subdivisions + 1) + column;
      const right = back + 1;
      const left = back + subdivisions + 1;
      const front = left + 1;
      for (const indices of [
        [back, right, front],
        [back, front, left],
      ]) {
        const source: Triangle = [
          sourcePoints[indices[0]!]!,
          sourcePoints[indices[1]!]!,
          sourcePoints[indices[2]!]!,
        ];
        const target: Triangle = [
          targetPoints[indices[0]!]!,
          targetPoints[indices[1]!]!,
          targetPoints[indices[2]!]!,
        ];
        const transform = triangleTransform(source, target);
        if (!transform) return undefined;
        triangles.push({ source, target, transform });
      }
    }
  }
  return triangles;
}

/** Draw once into a caller-managed ground texture; do not rebuild this mesh every animation frame. */
export function drawSettlementGroundTexture(
  context: SettlementGroundCanvas,
  image: CanvasImageSource,
  sourceQuad: SettlementGroundQuad,
  targetQuad: SettlementGroundQuad,
  subdivisions = 8,
): boolean {
  if (!Number.isInteger(subdivisions) || subdivisions < 1 || subdivisions > 16) return false;
  // Validate both full quads before the fast path: three valid corners alone cannot rule out
  // a crossed, concave or nonfinite fourth corner. Use the same domain as the mesh builder.
  if (!islandHomography(unitQuad, sourceQuad) || !islandHomography(unitQuad, targetQuad))
    return false;
  const affine = triangleTransform(
    [sourceQuad[0], sourceQuad[1], sourceQuad[2]],
    [targetQuad[0], targetQuad[1], targetQuad[2]],
  );
  if (affine) {
    const [a, b, c, d, e, f] = affine;
    const matches = sourceQuad.every((point, index) => {
      const target = targetQuad[index]!;
      const x = a * point.x + c * point.y + e;
      const y = b * point.x + d * point.y + f;
      // Floating-point roundoff only, not a visual approximation of a perspective warp.
      const tolerance =
        64 *
        Number.EPSILON *
        Math.max(
          1,
          Math.abs(a * point.x),
          Math.abs(c * point.y),
          Math.abs(e),
          Math.abs(b * point.x),
          Math.abs(d * point.y),
          Math.abs(f),
          Math.abs(target.x),
          Math.abs(target.y),
        );
      return (
        Number.isFinite(x) &&
        Number.isFinite(y) &&
        Math.abs(x - target.x) <= tolerance &&
        Math.abs(y - target.y) <= tolerance
      );
    });
    if (matches) {
      // A single outer clip avoids internal antialiasing seams on uniform/mirrored terrain.
      context.save();
      try {
        context.beginPath();
        context.moveTo(targetQuad[0].x, targetQuad[0].y);
        for (const point of targetQuad.slice(1)) context.lineTo(point.x, point.y);
        context.closePath();
        context.clip();
        context.transform(...affine);
        context.drawImage(image, 0, 0);
      } finally {
        context.restore();
      }
      return true;
    }
  }
  const mesh = createSettlementGroundMesh(sourceQuad, targetQuad, subdivisions);
  if (!mesh) return false;
  for (const triangle of mesh) {
    context.save();
    try {
      context.beginPath();
      context.moveTo(triangle.target[0].x, triangle.target[0].y);
      context.lineTo(triangle.target[1].x, triangle.target[1].y);
      context.lineTo(triangle.target[2].x, triangle.target[2].y);
      context.closePath();
      context.clip();
      context.transform(...triangle.transform);
      context.drawImage(image, 0, 0);
    } finally {
      context.restore();
    }
  }
  return true;
}

export function supportsSettlementWaterFoundation(buildingType: string): boolean {
  return settlementWaterFoundationManifest.buildingTypes.some((type) => type === buildingType);
}

/** The caller must supply an actual owned building; this helper never invents plots or coordinates. */
export function drawSettlementWaterFoundation(
  context: SettlementGroundCanvas,
  image: CanvasImageSource,
  buildingType: string,
  targetQuad: SettlementGroundQuad,
  subdivisions = 8,
): boolean {
  if (!supportsSettlementWaterFoundation(buildingType)) return false;
  return drawSettlementGroundTexture(
    context,
    image,
    settlementWaterFoundationManifest.sourceQuad,
    targetQuad,
    subdivisions,
  );
}
